import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { Home, Send, Moon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useToast } from '@/hooks/useToast';

export default function NightNote() {
  const [content, setContent] = useState('');
  const { user } = useCurrentUser();
  const { mutate: publish, isPending } = useNostrPublish();
  const { toast } = useToast();
  const navigate = useNavigate();

  useSeoMeta({ title: 'Night Note - Zenstr' });

  const post = () => {
    if (!content.trim()) return;
    publish(
      { kind: 1, content: `🌙 Good Night\n\n${content}\n\n#GoodNight #Zenstr`, tags: [['t', 'goodnight'], ['t', 'zenstr']] },
      {
        onSuccess: () => {
          toast({ title: 'Posted!' });
          setContent('');
          setTimeout(() => navigate('/'), 1500);
        }
      }
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border/50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🧘‍♀️</span>
            <span className="font-display font-semibold">Zenstr</span>
          </div>
          <Link to="/">
            <Button variant="ghost" size="sm"><Home className="w-4 h-4 mr-2" /> Dashboard</Button>
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-6 py-16 max-w-2xl">
        <div className="space-y-8">
          <div className="text-center space-y-2">
            <Moon className="w-16 h-16 mx-auto text-primary" />
            <h1 className="text-4xl md:text-5xl font-display font-bold">Good Night</h1>
            <p className="text-lg text-muted-foreground">Reflect and share your peace</p>
          </div>

          {!user ? (
            <Card className="p-12 text-center space-y-4 bg-card/50">
              <p className="text-muted-foreground">Log in to post</p>
              <LoginArea className="max-w-60 mx-auto" />
            </Card>
          ) : (
            <Card className="p-6 space-y-4 bg-card/50">
              <Textarea
                placeholder="Today I felt..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={8}
                className="resize-none text-base"
              />
              <Button onClick={post} disabled={isPending || !content.trim()} className="w-full py-6 text-lg">
                <Send className="w-5 h-5 mr-2" /> Share Night Note
              </Button>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
